export interface ValidationRule {
  type: string;
  params?: any;
  translationKey?: string;
  message?: string;
}

export interface FieldValidationResult {
  isValid: boolean;
  error?: string;
}

export type TFunction = (
  key: string,
  replacements?: Record<string, string | number>
) => string;

export function validateField(
  value: any,
  rules: ValidationRule[],
  fieldName: string,
  t: TFunction
): FieldValidationResult {
  const isEmpty =
    value === null || value === undefined || value === "";

  for (const rule of rules) {
    const result = applyValidationRule(
      value,
      rule,
      fieldName,
      isEmpty,
      t
    );
    if (!result.isValid) {
      return result;
    }
  }

  return { isValid: true };
}

function applyValidationRule(
  value: any,
  rule: ValidationRule,
  fieldName: string,
  isEmpty: boolean,
  t: TFunction
): FieldValidationResult {
  const { type, params, translationKey } = rule;

  switch (type) {
    case "is_required":
      if (isEmpty) {
        return {
          isValid: false,
          error: t(translationKey || "validationIsRequired"),
        };
      }
      break;

    case "is_date":
      if (!isEmpty) {
        const valueStr = String(value).trim();
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (!dateRegex.test(valueStr)) {
          return {
            isValid: false,
            error: t(translationKey || "validationDateFormat"),
          };
        }
      }
      break;

    case "is_int":
      if (!isEmpty) {
        const valueStr = String(value).trim();
        if (!/^-?\d+$/.test(valueStr)) {
          return {
            isValid: false,
            error: t(
              translationKey || "validationNumberMustBeInt"
            ),
          };
        }
      }
      break;

    case "is_double":
      if (!isEmpty) {
        const valueStr = String(value)
          .trim()
          .replace(",", ".");
        if (!/^-?\d+(\.\d+)?$/.test(valueStr)) {
          return {
            isValid: false,
            error: t(
              translationKey ||
              "validationNumberMustBeDouble"
            ),
          };
        }
      }
      break;

    case "max_length":
      if (!isEmpty && params != null) {
        const maxLen =
          typeof params === "number"
            ? params
            : params.length;
        const valueStr = String(value);

        if (valueStr.length > maxLen) {
          return {
            isValid: false,
            error: t(
              translationKey || "validationMaxLength",
              { params: maxLen }
            ),
          };
        }
      }
      break;

    case "min_length":
      if (!isEmpty && params != null) {
        const minLen =
          typeof params === "number"
            ? params
            : params.length;
        const valueStr = String(value);

        if (valueStr.length < minLen) {
          return {
            isValid: false,
            error: t(
              translationKey || "validationMinLength",
              { params: minLen }
            ),
          };
        }
      }
      break;
  }

  return { isValid: true };
}

export function applyTransformations(
  value: any,
  rules: ValidationRule[],
  fieldType?: string,
  isBeforeSave: boolean = false
): any {
  let result = value;

  for (const rule of rules) {
    if (rule.type === "replace" && rule.params) {
      const { pattern, replacement } = rule.params;
      if (pattern && result != null) {
        const regex = new RegExp(pattern, "g");
        result = String(result).replace(
          regex,
          replacement || ""
        );
      }
    }
  }

  const isTextArea =
    fieldType === "text" || fieldType === "textarea";

  if (typeof result === "string") {
    const needsTrim = rules.some((r) =>
      [
        "is_date",
        "is_int",
        "is_double",
        "is_unique",
        "is_regex",
        "max_length",
        "min_length",
      ].includes(r.type)
    );

    if (needsTrim || (isTextArea && isBeforeSave)) {
      if (!isTextArea || isBeforeSave) {
        result = result.trim();
      }
    }
  }

  return result;
}
